<template>
  <div class="gw-edition-steplist">
    <div class="v-centerer gw-edition-steplist__title">
      <h2 class="basic-sub-title">
        {{ $t("message.STEPS") }}
      </h2>
      <span style="margin-left: 50px;">
        {{ totalDurationString }}
      </span>
    </div>
    <div ref="stepList" class="generic-background">
      <GwStep
        v-for="(step, stepIdx) in gembaWalk.steps"
        :key="`${step.id}-${keyModifier}`"
        :index="stepIdx"
        :all-zones="allZones"
        :all-checklists="compChecklists"
        :value="step"
        :is-sortable="!isMobileLandscape"
        @delete="removeStep(step)"
        @newCheckList="createCheckList"
        @editChecklist="editChecklist"
      />
      <button
        class="generic-border generic-border--text-btn generic-border--white gw-edition-steplist__steps__add"
        @click="addStep"
      >
        <div class="generic-border__left">
          <Icon class="generic-border__left-blue-icon">{{ addExtraBold }}</Icon>
          <span>{{ $t('message.ADD_STEP') }}</span>
        </div>
        <Icon class="generic-border__right-arrow">{{ arrowDownLine }}</Icon>
      </button>
    </div>
    <transition name="fade" mode="out-in">
      <div v-if="newCheckList != null" class="dark-overlay v-centerer" @click="newCLClose">
        <div class="dark-overlay-content xlarge new-checklist" @click.stop="">
          <CloseButton @click.native="newCLClose" />
          <div class="f-h cl-edition-content" style="overflow-y:auto">
            <GwEditionChecklist :check-list="newCheckList" class="overlay-cl-item" :no-delete="true" />
            <div class="h-v-centerer">
              <button class="generic-border generic-border--text-btn generic-border--blue mt10" @click="newCLDone">
                {{ $t('message.OK') }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
/**
 * List of editable step items of a GW.
 */


import $ from 'jquery';
import { mapState, createNamespacedHelpers } from 'vuex';
// Components
import CloseButton from '@/components/close-button.vue';
import GwEditionChecklist from '@/components/gw-edition-checklist.vue';
import GwStep from '@/components/gw-step.vue';
import Icon from '@/components/icon.vue';
// Utils
import { FETCH_ZONES } from '@/store/action-types';
import { GET_ZONES } from '@/store/mutation-types';
import {
  CREATE_GW_CHECKLIST,
  SET_GW_STEP,
  DELETE_GW_STEP,
  FETCH_GW_CHECKLISTS,
  DELETE_GW_CHECKLIST,
} from '@/store/modules/gemba-walks/action-types';
import { GET_GW_CHECKLISTS } from '@/store/modules/gemba-walks/mutation-types';
import { calert, preventPastingStyles } from '@/utils/main';
import IAHelp from '@/utils/classes/IAHelp';
import { addExtraBold, arrowDownLine } from '@/assets/icons';

const { mapState: gwMapState } = createNamespacedHelpers('gembaWalks');


export default {

  name: 'GwEditionSteplist',

  components: {
    CloseButton,
    GwEditionChecklist,
    GwStep,
    Icon,
  },

  props: {
    gembaWalk: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      addExtraBold,
      arrowDownLine,
      // For sortable list
      keyModifier: 1,
      // Team picker
      newCheckList: null,
      newCLStep: null,
    };
  },

  computed: {
    totalDuration() {
      const totalDuration = this.$moment.duration();
      (this.gembaWalk.steps || []).forEach((step) => {
        totalDuration.add(Number(step.duration), 's');
      });
      return totalDuration;
    },
    totalDurationString() {
      if (!this.totalDuration.isValid()) {
        return '0:00:00';
      }
      const minutes = this.totalDuration.minutes();
      const seconds = this.totalDuration.seconds();
      return `${Math.floor(this.totalDuration.asHours())}:${
        minutes < 10 ? '0' : ''}${minutes}:${
        seconds < 10 ? '0' : ''}${seconds}`;
    },
    /** Array: array of checklists (if demo mode, demo checklists) */
    compChecklists() {
      return this.demoMode ? IAHelp.demoGWChecklists() : this.allChecklists;
    },
    /** Boolean: Return true if you are on mobile landscape */
    isMobileLandscape() {
      return this.$store.state.device.isSmartphoneLandscape;
    },
    ...mapState({
      allZones: state => state[GET_ZONES.stateKey] || [],
      allZonesLoading: state => state[GET_ZONES.loadingKey],
    }),
    ...gwMapState({
      allChecklists: state => state[GET_GW_CHECKLISTS.stateKey] || [],
      allChecklistsLoading: state => state[GET_GW_CHECKLISTS.loadingKey],
    }),
  },

  watch: {
    isMobileLandscape(e) {
      if (e) {
        $(this.$refs.stepList).sortable('destroy');
      } else {
        this.updateStepSortable();
      }
    },
  },

  created() {
    if (!this.allZonesLoading) {
      this.$store.dispatch(FETCH_ZONES);
    }
    if (!this.allChecklistsLoading) {
      this.$store.dispatch(`gembaWalks/${FETCH_GW_CHECKLISTS}`);
    }
    this.updateStepSortable();
  },

  beforeDestroy() {
    $(this.$refs.stepList).sortable('destroy');
  },

  methods: {
    addStep() {
      const number = this.gembaWalk.steps.length === 0
        ? 1
        : Math.max(...this.gembaWalk.steps.map(step => step.number)) + 1;
      this.$store.dispatch(
        `gembaWalks/${SET_GW_STEP}`,
        { id: 0, gw: this.gembaWalk.id, number },
      ).then(() => {
        this.$nextTick(this.updateStepSortable);
      });
    },
    /** Prompt the user to delete a step after clicking a delete button */
    removeStep(step) {
      calert(this.$t('message.GW_CONFIRM_REMOVE_STEP'), [
        {
          title: this.$t('message.YES'),
          sClass: 'red',
          action: () => {
            this.$store.dispatch(`gembaWalks/${DELETE_GW_STEP}`, { id: step.id, gw: step.gw })
              .then(this.updateStepSortable);
          },
        },
        {
          title: this.$t('message.CANCEL'),
        },
      ]);
    },
    /** Setup $.sotable to re-order steps */
    updateStepSortable() {
      this.$nextTick(() => {
        preventPastingStyles();
        $(this.$refs.stepList).sortable({
          items: '.gw-step',
          axis: 'y',
          opacity: 0.7,
          placeholder: 'gw-step__placeholder',
          helper: 'clone',
          update: (event, ui) => {
            // Steps r-ordered : Get the new list and send it as an XHR
            const fromIndex = ui.item.data('id');
            const toIndex = ui.item.index();
            if (fromIndex !== toIndex) {
              const stepIds = this.gembaWalk.steps.map(step => step.id);
              stepIds.splice(toIndex, 0, stepIds.splice(fromIndex, 1)[0]);
              const params = {
                id: stepIds[toIndex],
                gw: this.gembaWalk.id,
                order: stepIds,
              };
              $(this.$refs.stepList).sortable('destroy');
              this.$store.dispatch(`gembaWalks/${SET_GW_STEP}`, params).then(() => {
                this.keyModifier++;
                this.updateStepSortable();
              });
            }
          },
        });
      });
    },
    /** User might want to create a new CL from this page */
    createCheckList(editedStep) {
      this.$store.dispatch(`gembaWalks/${CREATE_GW_CHECKLIST}`, {}).then((checklists) => {
        if (checklists instanceof Array) {
          const id = Math.max(...checklists.map(cl => cl.id));
          this.newCheckList = { id, name: '', items: [] };
          this.newCLStep = editedStep;
        }
      });
    },
    /** User might want to create a new CL from this page */
    editChecklist(checklist) {
      this.newCheckList = checklist;
    },
    /** Cancel adding a checklist */
    newCLClose() {
      // If the newly created checklist hasn't been edited, delete it
      const { id, name, items } = this.newCheckList;
      if (this.newCLStep && this.newCheckList && name === '' && items.length === 0) {
        this.$store.dispatch(`gembaWalks/${DELETE_GW_CHECKLIST}`, { id });
      }
      this.newCheckList = null;
      this.newCLStep = null;
    },
    /** Done adding a checklist */
    newCLDone() {
      if (this.newCLStep && this.newCheckList) {
        const params = {
          id: this.newCLStep.id,
          gw: this.newCLStep.gw,
          checklists: [...this.newCLStep.checklists, this.newCheckList.id],
        };
        this.$store.dispatch(`gembaWalks/${SET_GW_STEP}`, params);
      }
      // Refresh checklists
      this.$store.dispatch(`gembaWalks/${FETCH_GW_CHECKLISTS}`);
      this.newCheckList = null;
      this.newCLStep = null;
    },
  },
};
</script>

<style lang="scss">
@import "@/assets/scss/vars.scss";

.overlay-cl-item {
  background: #efefef;
  text-align: left;
  margin-top: 50px;
}

.gw-edition-steplist {

  &__title {
    margin-bottom: 1rem;

    h2.basic-sub-title {
      margin-bottom: 0px;
    }
  }

  &__steps {
    &__add {
      margin-top: 1rem;
    }
  }
  .cl-edition-content {
    padding: 20px;
    box-sizing: border-box;
    overflow-y: auto;
  }

  .dark-overlay-content.new-checklist {
    background: $basic-section-background;

    .action-btn {
      margin-top: 0.7em;
    }
  }
}

</style>
